import React from "react";
import styled, { keyframes } from "styled-components";
import Button from "../../components/Button/Button";
import Text from "../../components/Text/Text";
import MoreHorizontal from "../../components/Svg/Icons/MoreHorizontal";
import { ButtonProps } from "../../components/Button";
import { connectorLocalStorageKey, walletLocalStorageKey } from "./config";
import { Login, Config } from "./types";

interface Props {
  walletConfig: Config;
  login: Login;
  onDismiss: () => void;
}

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

const WalletButton = styled(Button).attrs({ width: "100%", variant: "text" })`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  height: 64px;
  padding: 12px 16px;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  background: ${({ theme }) => theme.colors.backgroundAlt};
  transition: all 0.2s ease-in-out;
  position: relative;
  overflow: hidden;

  &:hover {
    background: ${({ theme }) => theme.colors.backgroundAlt2};
    border-color: ${({ theme }) => theme.colors.primary};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 212, 170, 0.15);

    .wallet-status {
      opacity: 1;
    }
  }

  &:active {
    transform: translateY(0);
    transition: transform 0.1s;
  }
`;

const WalletInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const WalletIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.backgroundAlt2};
  transition: transform 0.2s ease-in-out;
`;

const WalletText = styled(Text)`
  font-weight: 600;
  font-size: 16px;
  color: ${({ theme }) => theme.colors.text};
`;

const WalletStatus = styled.div`
  opacity: 0.6;
  transition: opacity 0.2s ease-in-out;
  font-size: 11px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSubtle};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

interface MoreWalletCardProps extends ButtonProps {
  t: (key: string) => string;
}

export const MoreWalletCard: React.FC<MoreWalletCardProps> = ({ t, ...props }) => {
  return (
    <WalletButton variant="tertiary" {...props}>
      <MoreHorizontal width="40px" mb="8px" color="textSubtle" />
      <Text fontSize="14px">{t("More")}</Text>
    </WalletButton>
  );
};

const WalletCard: React.FC<Props> = ({ login, walletConfig, onDismiss }) => {
  const { title, icon: Icon } = walletConfig;

  // Determine status based on wallet type
  const getWalletStatus = () => {
    if (title === "WalletConnect") return "QR CODE";
    if (title === "Metamask" && window.ethereum?.isMetaMask) return "INSTALLED";
    if (title === "Trust Wallet" && window.ethereum?.isTrust) return "INSTALLED";
    return "";
  };

  const status = getWalletStatus();

  return (
    <WalletButton
      onClick={() => {
        if (!window.ethereum && (title === "Metamask" || title === "Trust Wallet") && walletConfig.href) {
          window.open(walletConfig.href, "_blank", "noopener noreferrer");
        } else {
          login(walletConfig.connectorId);
          localStorage?.setItem(walletLocalStorageKey, walletConfig.title);
          localStorage?.setItem(connectorLocalStorageKey, walletConfig.connectorId);
          onDismiss();
        }
      }}
      id={`wallet-connect-${title.toLocaleLowerCase()}`}
    >
      <WalletInfo>
        <WalletIcon>
          <Icon width="32px" />
        </WalletIcon>
        <WalletText>{title}</WalletText>
      </WalletInfo>
      {status && (
        <WalletStatus className="wallet-status">
          {status}
        </WalletStatus>
      )}
    </WalletButton>
  );
};

export default WalletCard;
